import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCdnUrl } from "../../hooks/useCdnUrl";

import { useProductErrorsAndWarnings } from "../../hooks/useProductErrorsAndWarnings";

import { BadgeType, FloatingMenuButton } from "./floating-menu-button";
import { FloatingMenuContent } from "./floating-menu-content";

import { labels } from "./labels";
import "./floating-menu.css";
import { useKeyDown } from "../../hooks/useKeyboard";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useLabels } from "../../hooks/useLabels";
import { showSaveAndConfirmDialogAsync } from "../dialogs/save-dialog";
import { useAppContext } from "../../hooks/useAppContext";
import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useDashboardContext } from "../../contexts/dashboard-context";

export function FloatingMenu() {
	const { productErrorsAndWarnings } = useProductErrorsAndWarnings();
	const {
		context: { account },
		editor,
		googleAnalytics,
	} = useAppContext();
	const { busyBlocker } = useBusyBlockerContext();
	const { createErrorHandler } = useErrorHandling();

	const { addSnackbar } = useSnackbar();

	const [isOpen, setIsOpen] = useState(false);

	const menuRef = useRef<HTMLDivElement>(null);

	const [[saveNotification]] = useLabels([labels.saveNotification]);

	const appContext = useAppContext();
	const dashboard = useDashboardContext();

	const onSave = useCallback(() => {
		googleAnalytics.trackEventToGa({ trackingAction: "SaveProject" });

		const saveAsync = () =>
			showSaveAndConfirmDialogAsync({
				editor,
				busyBlocker,
				getIsSignedIn: () => !!(account?.type === eXpress.core.AccountType.Full),
			});

		saveAsync()
			.then((data) => {
				if (data && saveNotification) {
					addSnackbar({
						id: "save",
						text: saveNotification,
					});
				}
			})
			.catch(createErrorHandler());
	}, [
		account?.type,
		addSnackbar,
		busyBlocker,
		createErrorHandler,
		editor,
		googleAnalytics,
		saveNotification,
	]);

	useKeyDown(
		(event) => {
			// Save
			if (event.key === "s" && (event.ctrlKey || event.metaKey)) {
				// googleAnalytics.trackEventToGa({ trackingAction: "KeyboardSave" });
				event.preventDefault();
				event.stopPropagation();
				onSave();
			}
		},
		{
			enableOnFormTags: true,
		},
	);

	useKeyDown((event) => {
		const selectedZone = editor.selection.getCurrentZone();
		if (event.key === "c" && (event.ctrlKey || event.metaKey) && selectedZone) {
			if (appContext.clipboard?.canCopyToClipboard(selectedZone)) {
				appContext.clipboard.setZone(selectedZone);
			}
		} else if (event.key === "v" && (event.ctrlKey || event.metaKey)) {
			const zone = appContext.clipboard?.getZone();
			const destinationPage = editor.selection.getLogicalSelectedPage();
			if (zone && destinationPage && dashboard.canDuplicateZone(zone, destinationPage)) {
				dashboard.duplicateZone(zone, destinationPage);
			}
		}
	});

	const [isCdnUrlActive] = useCdnUrl();

	const onClick = useCallback(() => {
		if (isOpen) {
			googleAnalytics.trackEventToGa({ trackingAction: "MenuClosed" });
		} else {
			googleAnalytics.trackEventToGa({ trackingAction: "MenuClicked" });
		}
		setIsOpen(!isOpen);
	}, [googleAnalytics, isOpen]);

	const onClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const touchstartHandler = useCallback(
		(event: TouchEvent) => {
			if (!menuRef.current) return;

			if (!event.composedPath().includes(menuRef.current)) {
				onClose();
			}
		},
		[onClose],
	);

	useEffect(() => {
		document.addEventListener("touchstart", touchstartHandler, true);

		return () => {
			document.removeEventListener("touchstart", touchstartHandler, true);
		};
	}, [touchstartHandler]);

	const getBadgeType = () => {
		if (!productErrorsAndWarnings.length) {
			return null;
		}

		if (productErrorsAndWarnings.some((err) => err.isError)) {
			return BadgeType.error;
		}

		return BadgeType.warning;
	};

	const cssClasses = classNames("floating-menu", {
		"floating-menu--feature-warning": !!isCdnUrlActive,
	});

	return (
		<div className={cssClasses} ref={menuRef}>
			<FloatingMenuButton onClick={onClick} badgeType={getBadgeType()} />

			{isOpen && <FloatingMenuContent onClose={onClose} onSave={onSave} />}
		</div>
	);
}
